/** G2 四屏（订单 / 成交 / 持仓 / 逐单归因）共用的小件：账本选择器、「缺」标记、区块级错误。
 *
 * 账本选择器对应 paper-trading「分账本」：一次只看一本账，选中的运行写进路由参数 `run`，
 * 账目端点带 `?run_id=` 取那一本；不选时取当前账（`current_run`）。服务端不合并多本账，
 * 这里也不在前端拼账。
 */

import type { ReactNode } from "react";
import { useSearchParams } from "react-router";

import { describeError } from "../../api/errors";
import { useBookOptions, type BookParams, type LedgerEnvelope } from "../../api/g2";

/** 从路由参数读出当前选的是哪本账。 */
export function useBookParams(): [BookParams, (runId: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const runId = params.get("run");
  const select = (next: string | null) => {
    const copy = new URLSearchParams(params);
    if (next) copy.set("run", next);
    else copy.delete("run");
    copy.delete("id");
    setParams(copy);
  };
  return [{ run_id: runId }, select];
}

/** 账本选择器：选项来自 `GET /runs`；页头同时写明信封里这本账来自哪次运行。 */
export function BookSelector({ envelope }: { envelope: LedgerEnvelope | null }): ReactNode {
  const options = useBookOptions();
  const [book, select] = useBookParams();
  return (
    <span className="toolbar" style={{ marginBottom: 0 }}>
      <span className="faint">账本</span>
      <select
        aria-label="账本"
        value={book.run_id ?? ""}
        onChange={(event) => select(event.target.value || null)}
        disabled={!options.isSuccess}
      >
        <option value="">当前账（current_run）</option>
        {(options.data ?? [])
          .slice()
          .reverse()
          .map((row) => (
            <option key={row.run_id} value={row.run_id}>
              {row.mode ?? "?"} · {row.strategy_id ?? "—"} · {row.trade_date ?? row.run_id}
            </option>
          ))}
      </select>
      {options.isError ? <span className="neg">运行索引未取到</span> : null}
      <BookLabel envelope={envelope} />
    </span>
  );
}

export function BookLabel({ envelope }: { envelope: LedgerEnvelope | null }): ReactNode {
  if (!envelope) return null;
  if (!envelope.run_id) return <span className="faint">这本账：常驻当前账（未绑定运行）</span>;
  return (
    <span className="faint">
      这本账：<span className="mono">{envelope.run_id}</span>
      {envelope.mode ? ` · ${envelope.mode}` : ""}
      {envelope.strategy_id ? ` · ${envelope.strategy_id}` : ""}
      {envelope.trade_date ? ` · ${envelope.trade_date}` : ""}
    </span>
  );
}

/** 缺项统一渲染：显式写「缺」，不留空、不写 0。 */
export function Missing({ why }: { why?: string }): ReactNode {
  return (
    <span className="neg" title={why}>
      缺{why ? <span className="faint">（{why}）</span> : null}
    </span>
  );
}

/** 区块级错误：只让这一块显示端点与原因并给「重试」，其余区块照常。 */
export function InlineError({ endpoint, error, onRetry }: { endpoint: string; error: unknown; onRetry?: () => void }): ReactNode {
  const text = describeError(error);
  return (
    <div className="note bad">
      <b>{text.title}</b> · <code>{endpoint}</code>
      <div className="hint">{text.why}</div>
      {onRetry ? (
        <button type="button" className="btn sm" onClick={onRetry} style={{ marginTop: 4 }}>
          重试
        </button>
      ) : null}
    </div>
  );
}
